/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-explicit-any */

import IconCheckWhite from '@assets/icons/check-white.svg?react';
import {
 closestCenter,
 DndContext,
 DragOverlay,
 KeyboardSensor,
 PointerSensor,
 useSensor,
 useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
 Box,
 Button,
 CircularProgress,
 Dialog,
 DialogActions,
 DialogContent,
 Typography,
 useMediaQuery,
} from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import themeOptions from '../../../utils/theme';
import CloseButton from '../CloseButton';
import XTabs from '../XTabs';
import { TempField } from './DraggableField';
import FieldsModalCols from './FieldsModalCols';
import FieldsModalCurrent from './FieldsModalCurrent';
import { useFieldsSettings } from './useFieldsSettings';

interface FieldsModalProps {
 isOpen: boolean;
 handleClose: React.Dispatch<React.SetStateAction<boolean>>;
 type: 'sidebar' | 'edit' | null;
 //  setColumnState: any;
}

const FieldsModal = memo((props: FieldsModalProps) => {
 const { t } = useTranslation();
 const { isOpen, handleClose, type } = props;
 const {
  items,
  activeItem,
  handleDragStart,
  handleDragEnd,
  handleApply,
  removeSelectedField,
  removeAllSelectedFields,
  loadingClientConfigurations,
 } = useFieldsSettings({ type, handleClose });

 const isLoading = !!loadingClientConfigurations?.updateClientConfiguration;

 const sensors = useSensors(
  useSensor(PointerSensor),
  useSensor(KeyboardSensor, {
   coordinateGetter: sortableKeyboardCoordinates,
  }),
 );

 const isLargerThanMd = useMediaQuery(themeOptions().breakpoints.up('md'));

 // Create tab content for mobile view
 const mobileTabs = [
  {
   id: 'available-fields',
   label: t('colModal.availableFields', 'Visi laukeliai'),
   content: (
    <Box sx={{ height: 'calc(100vh - 200px)', pb: 4, overflowY: 'auto' }}>
     {type && <FieldsModalCols type={type} items={items} />}
    </Box>
   ),
  },
  {
   id: 'selected-fields',
   label: t('colModal.colsSelected', 'Pasirinkti laukeliai'),
   content: (
    <Box sx={{ height: 'calc(100vh - 200px)', pb: 4, overflowY: 'auto' }}>
     <FieldsModalCurrent
      selectedFields={items.selected}
      removeSelectedField={removeSelectedField}
      removeAllSelectedFields={removeAllSelectedFields}
     />
    </Box>
   ),
  },
 ];

 return (
  type && (
   <Dialog
    open={isOpen}
    onClose={() => handleClose(false)}
    fullWidth
    fullScreen={!isLargerThanMd}
    sx={{
     '& .MuiDialog-container': {
      '& .MuiPaper-root': {
       width: '100%',
       height: '100%',
       maxWidth: '630px',
      },
     },
    }}
   >
    <CloseButton closeDialog={() => handleClose(false)} />
    <DialogContent
     sx={{ p: 0, height: '100%', overflowY: { xs: 'hidden', md: 'auto' } }}
    >
     <Box
      sx={{
       fontSize: 14,
      }}
     >
      <Typography sx={{ fontSize: 18, p: 3 }}>Laukelių nustatymai</Typography>

      <Box
       sx={{
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 0, md: 3 },
        pb: { xs: 0, md: 3 },
        width: '100%',
       }}
      >
       <Box sx={{ height: '100%' }}>
        {isLargerThanMd ? (
         // Desktop view: side by side layout
         <Box sx={{ display: 'flex', gap: 3, height: 'calc(100% - 66px)' }}>
          <DndContext
           sensors={sensors}
           collisionDetection={closestCenter}
           onDragStart={handleDragStart}
           onDragEnd={handleDragEnd}
          >
           {type && <FieldsModalCols type={type} items={items} />}
           <FieldsModalCurrent
            selectedFields={items.selected}
            removeSelectedField={removeSelectedField}
            removeAllSelectedFields={removeAllSelectedFields}
           />

           <DragOverlay>
            {activeItem ? <TempField item={activeItem} /> : null}
           </DragOverlay>
          </DndContext>
         </Box>
        ) : (
         // Mobile view: tabs layout
         <Box sx={{ height: 'calc(100% - 66px)' }}>
          <DndContext
           sensors={sensors}
           collisionDetection={closestCenter}
           onDragStart={handleDragStart}
           onDragEnd={handleDragEnd}
          >
           <XTabs
            tabs={mobileTabs}
            defaultTab={0}
            variant="fullWidth"
            ariaLabel="Fields Modal Tabs"
            sx={{ height: '100%' }}
            panelSx={{ height: 'calc(100% - 48px)', p: 0 }}
           />

           <DragOverlay>
            {activeItem ? <TempField item={activeItem} /> : null}
           </DragOverlay>
          </DndContext>
         </Box>
        )}
       </Box>
      </Box>
     </Box>
    </DialogContent>
    <DialogActions>
     <Box
      sx={{
       justifyContent: 'flex-end',
       display: 'flex',
       gap: 1,
      }}
     >
      <Button
       variant="contained"
       endIcon={
        isLoading ? (
         <CircularProgress
          color="inherit"
          size={16}
          sx={{ pointerEvents: 'none' }}
         />
        ) : (
         <IconCheckWhite style={{ width: 16, height: 11 }} />
        )
       }
       onClick={handleApply}
       disabled={isLoading}
      >
       {t('global.apply')}
      </Button>
     </Box>
    </DialogActions>
   </Dialog>
  )
 );
});
FieldsModal.displayName = 'FieldsModal';
export default FieldsModal;
